export const DEFAULT_USER_AVATAR_URL = '/assets/icons/default_avatar.svg';
export const SPORT_ICON_BASE = '/assets/icons/sport';
export const DEFAULT_SPORT_ICON_URL = `${SPORT_ICON_BASE}/allsports.svg`;
export const DEFAULT_SPORT_CARD_BG_URL = '/assets/images/events/default_bg.svg';

const KNOWN_SPORT_ICON_KEYS = new Set([
  'badminton',
  'basketball',
  'bike',
  'climbing',
  'football',
  'golf',
  'hiking',
  'paddle',
  'running',
  'swim',
  'table-tennis',
  'tennis',
  'trail',
  'trampoline',
]);

export function isSportIconUrl(url?: string | null): boolean {
  const value = url?.trim();
  return !!value && value.includes('/assets/icons/sport/');
}

export function getSportIconPath(sportKey?: string | null): string {
  const key = sportKey?.trim().toLowerCase();
  if (key && KNOWN_SPORT_ICON_KEYS.has(key)) {
    return `${SPORT_ICON_BASE}/${key}.svg`;
  }
  return DEFAULT_SPORT_ICON_URL;
}

function isUsableImageUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) || /^data:image\//i.test(url);
}

function resolveImageUrl(url: string | null | undefined, fallback: string): string {
  const value = url?.trim();
  if (!value || !isUsableImageUrl(value)) {
    return fallback;
  }
  return value;
}

export function hasUsableUserAvatar(avatar?: string | null): boolean {
  const value = avatar?.trim();
  return !!value && isUsableImageUrl(value);
}

export function sanitizeUserAvatarUrl(avatar?: string | null): string {
  return hasUsableUserAvatar(avatar) ? avatar!.trim() : '';
}

export function resolveUserAvatarInitials(
  firstName?: string | null,
  lastName?: string | null,
): string {
  const first = firstName?.trim();
  const last = lastName?.trim();

  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  if (first) {
    return first.charAt(0).toUpperCase();
  }
  return '';
}

export type UserAvatarDisplayMode = 'image' | 'initials' | 'default';

export type UserAvatarDisplay = {
  mode: UserAvatarDisplayMode;
  src?: string;
  label?: string;
};

export function resolveUserAvatarDisplay(params: {
  avatar?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  imageFailed?: boolean;
}): UserAvatarDisplay {
  if (!params.imageFailed && hasUsableUserAvatar(params.avatar)) {
    return { mode: 'image', src: params.avatar!.trim() };
  }

  const label = resolveUserAvatarInitials(params.firstName, params.lastName);
  if (label) {
    return { mode: 'initials', label };
  }

  return { mode: 'default', src: DEFAULT_USER_AVATAR_URL };
}

/** @deprecated Use sanitizeUserAvatarUrl + resolveUserAvatarDisplay for user avatars. */
export function resolveUserAvatarUrl(avatar?: string | null): string {
  return resolveImageUrl(avatar, DEFAULT_USER_AVATAR_URL);
}

export function resolveActivityImageUrl(image?: string | null, sportKey?: string | null): string {
  return resolveImageUrl(image, getSportIconPath(sportKey));
}

export function resolveGroupImageUrl(image?: string | null, sportKey?: string | null): string {
  return resolveImageUrl(image, getSportIconPath(sportKey));
}

export function applyDefaultImageOnError(event: Event, fallbackUrl: string): void {
  const img = event.target as HTMLImageElement | null;
  if (!img || img.dataset['fallbackApplied'] === 'true') {
    return;
  }
  img.dataset['fallbackApplied'] = 'true';
  img.src = fallbackUrl;
}
